import { Emitter } from "./emitter";
import type { EnvPin } from "./env";
import { CloseCode, type ClientConfig, type ClientInfo, type SessionBootstrapResponse } from "./protocol";
import { ConnectionError, ServerError, type GameSocket, type SocketStatus } from "./socket";

/** This device's id (platform/deviceId.ts in the app, a fake in tests). */
export interface DeviceIdSource {
  /** The stored device id; generates and stores one on the first launch. Rejects when storage fails. */
  getOrCreate(): Promise<string>;
}

export type FatalReason =
  | { kind: "replaced" }
  | { kind: "outdated"; minVersion?: string; storeUrl?: string }
  | { kind: "closed"; reason: string };

export type SessionState =
  | { kind: "connecting" }
  | { kind: "reconnecting"; attempt: number }
  | { kind: "ready"; playerId: string }
  | { kind: "paused" }
  | { kind: "maintenance"; until?: number }
  | { kind: "fatal"; reason: FatalReason };

export interface SessionEvents {
  state: SessionState;
  /** After every (re)connect, login and bootstrap. */
  ready: SessionBootstrapResponse;
  /** A newer config exists; call refreshConfig() at a safe point. */
  configPending: number;
  /** refreshConfig() fetched a config with a new version. */
  config: ClientConfig;
}

/**
 * Logs in with the device id on every connection (the first login creates the player), keeps the
 * player's env pinned, and bootstraps the game.
 */
export class Session {
  private readonly events = new Emitter<SessionEvents>();
  private current: SessionState = { kind: "connecting" };
  private wasReady = false;
  private connection = 0;
  private config: ClientConfig | undefined;
  private pendingVersion: number | undefined;
  private refreshing: Promise<ClientConfig> | undefined;

  constructor(
    private readonly socket: GameSocket,
    private readonly device: DeviceIdSource,
    private readonly client: ClientInfo,
    /** The socket URL must carry `env.current` as `?env=` (see withEnv). */
    private readonly env: EnvPin,
  ) {
    socket.onStatus((status) => this.onSocketStatus(status));
    socket.onPush("config.updated", ({ version }) => this.onConfigUpdated(version));
  }

  get state(): SessionState {
    return this.current;
  }

  get pendingConfigVersion(): number | undefined {
    return this.pendingVersion;
  }

  on<K extends keyof SessionEvents>(event: K, listener: (value: SessionEvents[K]) => void): () => void {
    return this.events.on(event, listener);
  }

  start(): void {
    this.socket.start();
  }

  /** Disconnects while the app is in the background and reconnects on return. */
  setActive(active: boolean): void {
    if (active) this.socket.resume();
    else this.socket.pause();
  }

  /** Connects again after a fatal state, e.g. when the player picks "play on this device". */
  retry(): void {
    if (this.current.kind === "fatal") this.socket.start();
  }

  /** Fetches the current config; emits "config" when its version changed. */
  refreshConfig(): Promise<ClientConfig> {
    this.refreshing ??= this.socket
      .request("config.get", {})
      .then((config) => {
        this.applyConfig(config, true);
        return config;
      })
      .finally(() => {
        this.refreshing = undefined;
      });
    return this.refreshing;
  }

  private onSocketStatus(status: SocketStatus): void {
    switch (status.kind) {
      case "idle":
        return;
      case "connecting":
        this.setState(this.wasReady || status.attempt > 0 ? { kind: "reconnecting", attempt: status.attempt } : { kind: "connecting" });
        return;
      case "waiting":
        if (status.maintenance) this.setState(status.until === undefined ? { kind: "maintenance" } : { kind: "maintenance", until: status.until });
        else this.setState({ kind: "reconnecting", attempt: status.attempt + 1 });
        return;
      case "open":
        this.connection = status.connection;
        void this.handshake(status.connection);
        return;
      case "paused":
        this.setState({ kind: "paused" });
        return;
      case "stopped":
        if (this.current.kind !== "fatal") this.setState({ kind: "fatal", reason: fatalFromClose(status.code, status.reason) });
        return;
    }
  }

  private async handshake(connection: number): Promise<void> {
    const stale = () => connection !== this.connection || this.socket.status.kind !== "open";
    try {
      const deviceId = await this.device.getOrCreate();
      if (stale()) return;
      const login = await this.socket.request("auth.login", { deviceId, client: this.client });
      if (!this.env.set(login.env)) console.warn("auth.login returned no valid env");
      if (stale()) return;
      const boot = await this.socket.request("session.bootstrap", {});
      if (stale()) return;
      this.applyConfig(boot.config, false);
      this.wasReady = true;
      this.setState({ kind: "ready", playerId: boot.player.id });
      this.events.emit("ready", boot);
    } catch (err) {
      // Stored even when the 4307 close came first, so the next connection already uses it.
      const wrongEnv = err instanceof ServerError && err.code === "wrong_env" && this.env.set(err.details?.env);
      if (stale()) return;
      if (wrongEnv) this.socket.redirect("wrong env");
      else this.onHandshakeError(err);
    }
  }

  private onHandshakeError(err: unknown): void {
    if (err instanceof ServerError) {
      const details = err.details ?? {};
      switch (err.code) {
        case "client_outdated":
          this.fail({ kind: "outdated", ...stringProps(details, "minVersion", "storeUrl") });
          return;
        case "maintenance":
          this.socket.reconnect("maintenance");
          return;
      }
    }
    if (err instanceof ConnectionError && err.reason === "closed") return;
    console.error("session handshake failed", err);
    this.socket.reconnect("handshake failed");
  }

  private fail(reason: FatalReason): void {
    this.setState({ kind: "fatal", reason });
    this.socket.stop(reason.kind);
  }

  private onConfigUpdated(version: number): void {
    if (!Number.isSafeInteger(version)) return;
    if (this.config && version <= this.config.version) return;
    if (this.pendingVersion !== undefined && version <= this.pendingVersion) return;
    this.pendingVersion = version;
    this.events.emit("configPending", version);
  }

  private applyConfig(config: ClientConfig, emit: boolean): void {
    const changed = config.version !== this.config?.version;
    this.config = config;
    if (this.pendingVersion !== undefined && this.pendingVersion <= config.version) this.pendingVersion = undefined;
    if (emit && changed) this.events.emit("config", config);
  }

  private setState(state: SessionState): void {
    this.current = state;
    this.events.emit("state", state);
  }
}

function fatalFromClose(code: number | undefined, reason: string): FatalReason {
  switch (code) {
    case CloseCode.replaced:
      return { kind: "replaced" };
    case CloseCode.clientOutdated:
      return { kind: "outdated" };
    default:
      return { kind: "closed", reason };
  }
}

function stringProps<K extends string>(source: Record<string, unknown>, ...keys: K[]): Partial<Record<K, string>> {
  const out: Partial<Record<K, string>> = {};
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string") out[key] = value;
  }
  return out;
}
